'use client';

import { useId } from 'react';
import { lookupIcon } from 'heroicons-lookup';

import UiUtils from '@utils/ui';

export default function ButtonV1(props) {
    const { 
        children, 
        size, 
        variant, 
        label, 
        leftIcon,
        leftIconSVG, 
        leftIconFill,
        rightIcon, 
        rightIconSVG, 
        rightIconFill,
        iconType, 
        className,
        ariaLabel, 
        disabled, 
        onClick, 
        ...otherProps 
    } = props;
    let buttonClass = 'flex justify-center items-center gap-1.5 font-vg-medium border rounded-lg';
    switch (size) {
        case 'small':
            buttonClass = `h-10 px-4 ${buttonClass}`;
            break;
        case 'large':
            buttonClass = `h-[52px] px-6 ${buttonClass}`;
            break;
        case 'x-small':
            buttonClass = `h-8 px-2 ${buttonClass}`;
            break;
        default:
            break;
    }
    let useVariant = disabled ? (variant === "navlink" ? "navlink-disabled" : "disabled") : variant; 
    switch (useVariant) {
        case 'solid':
            buttonClass = `${buttonClass} text-base leading-120 text-white bg-blue border-blue hover:bg-blue02 hover:border-blue02 active:bg-blue01 active:border-blue01 cursor-pointer`;
            break;
        case 'outline':
            buttonClass = `${buttonClass} text-base leading-120 bg-white text-blue border-blue hover:text-blue02 hover:border-blue02 active:text-blue01 active:border-blue01 cursor-pointer`;
            break;
        case 'ghost':
            buttonClass = `${buttonClass} text-base leading-120 bg-transparent border-white text-blue hover:text-blue02 active:text-blue01 cursor-pointer`;
            break;
        case 'indicator-ghost':
            buttonClass = `${buttonClass} text-sm leading-120 bg-transparent border-white text-blue hover:text-blue02 active:text-blue01 cursor-pointer`;
            break;
        case 'navlink':
            buttonClass = `${buttonClass} text-base leading-120 bg-white border-white text-grey hover:text-grey active:text-grey cursor-pointer`;
            break;
        case 'delete':
            buttonClass = `${buttonClass} text-base leading-120 bg-white text-red border-red hover:text-red hover:border-red active:text-red active:border-red cursor-pointer`;
            break;
        case 'disabled':
            buttonClass = `${buttonClass} text-base leading-120 bg-white text-grey border-grey !cursor-not-allowed`;
            break;
        case 'navlink-disabled':
            buttonClass = `${buttonClass} text-base leading-120 bg-white text-grey border-white !cursor-not-allowed`;
            break;
        case 'alert-success':
            buttonClass = `${buttonClass} text-sm bg-white text-dark-green01 border-dark-green01 min-w-15 cursor-pointer`;
            break;
        case 'alert-warning':
            buttonClass = `${buttonClass} text-sm bg-white text-dark-yellow02 border-dark-yellow02 min-w-15 cursor-pointer`;
            break;
        case 'alert-error':
            buttonClass = `${buttonClass} text-sm bg-white text-red02 border-red02 min-w-15 cursor-pointer`;
            break;
        case 'alert-information':
            buttonClass = `${buttonClass} text-sm bg-white text-blue01 border-blue01 min-w-15 cursor-pointer`;
            break;
        case 'alert-neutral':
            buttonClass = `${buttonClass} text-sm bg-white text-blue border-blue min-w-15 cursor-pointer`;
            break;
        case 'alert-neutral-solid':
            buttonClass = `${buttonClass} text-sm bg-blue text-white border-blue min-w-15 cursor-pointer`;
            break;
        default:
            break;
    }
    if (className) {
        buttonClass = `${buttonClass} ${className}`;
    }

    let IconLeft = null;
    let IconRight = null;

    const iconLibrary = iconType ? iconType : "outline";

    if (leftIcon) {
        IconLeft = lookupIcon(leftIcon, iconLibrary);
    }

    if (rightIcon) {
        IconRight = lookupIcon(rightIcon, iconLibrary);
    }

    let ariaLabelId = useId();

    const getIconLeft = () => {
        return (
            <>
                {IconLeft && (<IconLeft className="h-5"/>)}
                {leftIconSVG && leftIconFill && (
                    UiUtils.getSVGIcon(leftIconSVG, leftIconFill, "h-5")
                )}
            </>
        );
    }

    const getIconRight = () => {
        return (
            <>
                {IconRight && (<IconRight className="h-5"/>)}
                {rightIconSVG && leftIconFill && (
                    UiUtils.getSVGIcon(rightIconSVG, rightIconFill, "h-5")
                )}
            </>
        );
    }

    return (
        <>
            {ariaLabel ? (
                <button
                    className={buttonClass}
                    onClick={(e) => {
                        if (onClick) {
                            onClick(e);
                        }
                    }}
                    disabled={useVariant === "disabled"}
                    aria-labelledby={ariaLabelId}
                    { ...otherProps } 
                >
                    {getIconLeft()}
                    {label}
                    {getIconRight()}
                    <span id={ariaLabelId} className="sr-only">{ariaLabel}</span>
                </button>
            ) : (
                <button
                    className={buttonClass}
                    onClick={(e) => {
                        if (onClick) {
                            onClick(e);
                        }
                    }}
                    disabled={useVariant === "disabled"}
                    { ...otherProps } 
                >
                    {getIconLeft()}
                    {label}
                    {getIconRight()}
                </button>
            )}
        </>
    );
}